'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { GiftCouponValue } from '@eliadmin/core/src/components/product/gift-coupon-value';
import { GiftPackageLabel } from '@eliadmin/core/src/components/product/gift-package-label';
import { getGiftItemDisplay } from '@eliadmin/core/src/utils/cart-gift-utils';
import { IoGiftOutline } from 'react-icons/io5';
import type { ProductGift } from '@eliadmin/core/src/api/products/types/product-types';

function SingleTierGift({ rule }: { rule: ProductGift }) {
  const tier = rule.tiers[0];
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const { t } = useTranslations();
  // The product preview cannot determine cart eligibility from the selected product price.
  const condition = tier.order_price !== null && Number(tier.order_price) > 0
    ? `${t('text3229')} ${formatPrice(tier.order_price, currency, currencySettings).replace(/([.,])0+(\s|$)/, '$2')}` : null;
  const badge = condition && <span className="ea-product-detail-gifts__item-price w-fit shrink-0 rounded-full bg-primary/10 px-2 py-1 text-[10px] font-bold leading-none text-primary sm:text-[11px]">{condition}</span>;
  return <div data-gift-id={rule.id} data-gift-tier-id={tier.id} data-gift-presentation="single"
    className="ea-product-detail-gifts__item flex w-full flex-col gap-2 rounded-[8px] bg-[#F5F5F5] p-2.5">
    {tier.reward_coupon && <div className="ea-product-detail-gifts__coupon flex min-w-0 items-center gap-2.5">
      <GiftCouponValue coupon={tier.reward_coupon} className="ea-product-detail-gifts__coupon-value h-10 w-10 rounded-md border-0 bg-primary/10 text-xs" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="ea-product-detail-gifts__coupon-name min-w-0 text-xs font-bold text-foreground sm:text-sm">{tier.reward_coupon.name}</span>
        <span className="text-[11px] text-[#838383]">{rule.name}</span>
      </div>
      {badge}
    </div>}
    {tier.items.map((item, index) => {
      const display = getGiftItemDisplay(item);
      return <div key={item.id} className="flex min-w-0 items-center gap-2.5">
        <Image src={display.image?.url ?? '/images/placeholder.png'} alt={resolveImageAlt(display.image, display.name, rule.name)}
          width={40} height={40} className="h-10 w-10 shrink-0 rounded-md object-cover" unoptimized />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-xs font-bold text-foreground sm:text-sm">{Number(display.quantity) !== 1 && <>{display.quantity} × </>}{display.name}</span>
          <span className="text-[11px] text-[#838383]">{rule.name}</span>
        </div>
        {index === 0 && !tier.reward_coupon && badge}
      </div>;
    })}
    {!tier.reward_coupon && !tier.items.length && <div className="flex min-w-0 items-center gap-2.5">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><IoGiftOutline aria-hidden="true" className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1 text-xs font-bold text-foreground sm:text-sm">{rule.name}</span>
      {badge}
    </div>}
  </div>;
}

export function IvatoProductGifts() {
  const product = useVariantsStore(state => state.product)!;
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const { t } = useTranslations();
  if (!product.gifts?.length) return null;
  return <div className={cn('flex w-full flex-col gap-2', 'ea-product-detail-gifts')}>
    <h2 className="text-xs font-semibold text-foreground sm:text-sm">{t('text664')}</h2>
    {product.gifts.map(rule => rule.tiers.length === 1 ? <SingleTierGift key={rule.id} rule={rule} /> : <section key={rule.id} data-gift-id={rule.id} className="flex w-full flex-col gap-2">
      <h3 className="text-xs font-bold text-foreground sm:text-sm">{rule.name}</h3>
      {rule.tiers.map(tier => <div key={tier.id} data-gift-tier-id={tier.id} className={cn('flex w-full flex-col gap-2 rounded-[8px] bg-[#F5F5F5] p-2.5', 'ea-product-detail-gifts__item')}>
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="min-w-0 flex-1 text-xs font-bold text-foreground sm:text-sm">{tier.name}</span>
          {tier.order_price !== null && <span className={cn('w-fit shrink-0 rounded-full bg-primary/10 px-2 py-1 text-[10px] font-bold leading-none text-primary sm:text-[11px]', 'ea-product-detail-gifts__item-price')}>
            {t('text3229')} {formatPrice(tier.order_price, currency, currencySettings)}
          </span>}
        </div>
        <span className="text-[11px] text-[#838383]">{rule.name}</span>
        {tier.reward_coupon && <div className={cn('flex min-w-0 items-center gap-2.5', 'ea-product-detail-gifts__coupon')}>
          <GiftCouponValue coupon={tier.reward_coupon} className="h-10 w-10 rounded-md border-0 bg-primary/10 text-xs ea-product-detail-gifts__coupon-value" />
          <span className={cn('min-w-0 text-xs font-bold text-foreground sm:text-sm', 'ea-product-detail-gifts__coupon-name')}>{tier.reward_coupon.name}</span>
        </div>}
        {tier.items.length > 0 && <GiftPackageLabel />}
        {tier.items.map(item => {
          const display = getGiftItemDisplay(item);
          return <div key={item.id} className="flex min-w-0 items-center gap-2.5">
            <Image src={display.image?.url ?? '/images/placeholder.png'} alt={resolveImageAlt(display.image, display.name, tier.name)} width={40} height={40} className="h-10 w-10 shrink-0 rounded-md object-cover" unoptimized />
            <span className="text-xs font-bold text-foreground sm:text-sm">{display.quantity} × {display.name}</span>
          </div>;
        })}
        {!tier.reward_coupon && !tier.items.length && <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><IoGiftOutline aria-hidden="true" className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1 text-xs font-bold text-foreground sm:text-sm">{tier.name}</span>
        </div>}
      </div>)}
    </section>)}
  </div>;
}
